import { useMemo } from 'react';
import useCartState from '../../../../hooks/useCartState';
import { useProfileLinks } from '../../../../hooks/useUserData';
import { hasSubscription } from '../../cart-items/util';
import { Helpers } from '../../../../core/src/helpers';

export const CTA_TYPES = {
    AUTH: 'auth',
    CHECKOUT: 'checkout',
};

/**
 * Custom hook to determine which checkout CTA (Call To Action) to display
 * @returns {Object} Object containing:
 *   - ctaType: 'auth' | 'checkout' - which type of CTA to show
 *   - isLoggedIn: boolean - whether user is logged in
 *   - isSecureLinkUser: boolean - whether user is a secure link user
 *   - hasSub: boolean - whether cart has subscription items
 *   - checkoutUrl: string - URL for checkout
 *   - loginUrl: string - URL for sign in
 *   - registrationUrl: string - URL for account creation
 *   - logonUrl: string - fallback logon URL
 */
const useCheckoutCTA = () => {
    const { cartInfo } = useCartState();
    const { logonUrl, registerUrl } = useProfileLinks(['hpRewardsUrl', 'logonUrl', 'registerUrl']);

    const ctaData = useMemo(() => {
        const { cartView } = cartInfo || {};
        const { x_checkoutUrl, x_userType, x_hpIdRegisterCart, x_hpIdLoginCart, x_isSecureLinkUser } = cartView || {};

        const hasSub = hasSubscription(cartView?.orderItem);
        const isLoggedIn = x_userType === 'R';
        const isSecureLinkUser = x_isSecureLinkUser === 'true' && !isLoggedIn;
        const registrationUrl = isSecureLinkUser ? registerUrl : x_hpIdRegisterCart;

        // Determine which CTA type to show
        const shouldShowAuth = (hasSub && !isLoggedIn) || isSecureLinkUser;
        const ctaType = shouldShowAuth ? CTA_TYPES.AUTH : CTA_TYPES.CHECKOUT;

        return {
            ctaType,
            isLoggedIn,
            isSecureLinkUser,
            hasSub,
            checkoutUrl: x_checkoutUrl,
            loginUrl: x_hpIdLoginCart || logonUrl,
            registrationUrl:
                registrationUrl || Helpers.addUrlParamV2(logonUrl, { state: 'operation:registerloyaltycartpage' }),
            logonUrl,
        };
    }, [cartInfo, logonUrl, registerUrl]);

    return ctaData;
};

export default useCheckoutCTA;
